<template>
  <div class="ion-padding">
    <div v-if="mediaItems.length === 0 && !loading && currentUserType === 'instructor'" class="ion-text-center ion-padding" style="color: var(--ion-color-medium);">
      <ion-icon :icon="imagesOutline" size="large" color="medium" />
      <p>No photos or videos uploaded yet.</p>
    </div>

    <ion-spinner v-if="loading" name="crescent" />

    <div v-if="mediaItems.length > 0" class="media-grid">
      <MediaThumbnail
        v-for="(media, index) in mediaItems"
        :key="media.id"
        :media="media"
        :index="index"
        @click="openMediaViewer(media)"
      />
    </div>
    <div v-if="currentUserType === 'instructor'">
      <ion-button
        expand="block" fill="clear" color="tertiary"
        @click="openUploadWidget"
      >
        <ion-icon slot="start" :icon="cloudUploadOutline" />
        Add Photos & Videos
      </ion-button>
    </div>
  </div>
</template>

<script setup>
import { IonButton, IonIcon, IonSpinner, alertController, loadingController, modalController, toastController } from '@ionic/vue'
import { addDoc, collection, deleteDoc, doc, orderBy, query, serverTimestamp, where } from 'firebase/firestore'
import { cloudUploadOutline, imagesOutline } from 'ionicons/icons'
import { computed, ref, watch } from 'vue'
import { useCollection, useFirestore } from 'vuefire'

// Local components and composables
import MediaThumbnail from './MediaList/MediaThumbnail.vue'
import MediaViewerModal from './MediaList/MediaViewerModal.vue'
import { trackEvent } from '@/globals'
import { useCloudinaryUpload } from './MediaList/useCloudinaryUpload'
import { useCloudinaryUrls } from './MediaList/useCloudinaryUrls'

const props = defineProps({
  lesson: { type: Object, required: true },
  currentUserType: { type: String, required: true, default: 'student' },
})

// Firestore setup
const db = useFirestore()
const mediaCollection = collection(db, 'lessonMedia')

// State
const loading = ref(true)
const currentModal = ref(null)

// Get media items for this lesson
const mediaQuery = computed(() => {
  return query(
    mediaCollection,
    where('lessonId', '==', props.lesson.id),
    orderBy('uploadedAt', 'desc'),
  )
})

const { data: mediaItems } = useCollection(mediaQuery, {
  ssrKey: `lesson-media-${props.lesson.id}`,
})

// Upload functionality
const { openUploadWidget } = useCloudinaryUpload(
  props.lesson,
  handleUploadSuccess,
  handleUploadError,
)

// URL generation
const { getDownloadUrl, getImageLargeUrl } = useCloudinaryUrls()

// Watch for changes in media items
watch(mediaItems, () => {
  loading.value = false
}, { immediate: true })

// Expose closeMediaViewer for parent component
defineExpose({
  closeMediaViewer,
})

// Upload handlers
async function handleUploadSuccess(mediaInfo) {
  const mediaDoc = {
    lessonId: props.lesson.id,
    studentId: props.lesson.studentId,
    instructorId: props.lesson.instructorId,
    publicId: mediaInfo.public_id,
    originalFilename: mediaInfo.original_filename,
    resourceType: mediaInfo.resource_type,
    format: mediaInfo.format,
    bytes: mediaInfo.bytes,
    width: mediaInfo.width,
    height: mediaInfo.height,
    uploadedAt: serverTimestamp(),
  }

  if (mediaInfo.duration) mediaDoc.duration = mediaInfo.duration

  try {
    await addDoc(mediaCollection, mediaDoc)
    await showToast('Media uploaded successfully!', 'success')
    trackEvent('Uploaded Media', { mediaType: mediaInfo.resource_type })

    // Preload transformed images to cache them on Cloudinary
    if (mediaInfo.resource_type === 'image') preloadTransformedImage(mediaDoc)
  } catch (error) {
    console.error('❌ Error saving media to Firestore:', error)
    await showToast('Error saving media information', 'danger')
  }
}

/**
 * Preload transformed image to trigger Cloudinary caching
 */
function preloadTransformedImage(media) {
  const img = document.createElement('img')
  img.style.position = 'absolute'
  img.style.top = '-9999px'
  img.style.left = '-9999px'
  img.style.width = '1px'
  img.style.height = '1px'
  img.style.opacity = '0'
  img.style.pointerEvents = 'none'

  // Set src to the large watermarked version to cache it
  img.src = getImageLargeUrl(media)

  // Clean up after loading (success or error)
  const cleanup = () => {
    if (img.parentNode) img.parentNode.removeChild(img)
  }

  img.onload = cleanup
  img.onerror = cleanup

  // Add to DOM to trigger the load
  document.body.appendChild(img)

  // Failsafe cleanup after 10 seconds
  setTimeout(cleanup, 10000)
}

function handleUploadError(message) {
  showToast(message, 'danger')
}

// Media viewer functions
async function openMediaViewer(media) {
  trackEvent('Opened Media', { mediaType: media.resourceType, currentUserType: props.currentUserType })
  const modal = await modalController.create({
    component: MediaViewerModal,
    componentProps: {
      media,
      currentUserType: props.currentUserType,
      onDownload: downloadMedia,
      onDelete: deleteMedia,
    },
  })

  currentModal.value = modal
  await modal.present()

  await modal.onWillDismiss()
  currentModal.value = null
}

async function closeMediaViewer() {
  if (currentModal.value) {
    await currentModal.value.dismiss()
    currentModal.value = null
  }
}

// Media actions
async function downloadMedia(media) {
  trackEvent('Downloaded Media', { mediaType: media.resourceType, currentUserType: props.currentUserType })
  try {
    const downloadUrl = getDownloadUrl(media)
    const publicIdParts = media.publicId.split('/')
    const baseFilename = publicIdParts[publicIdParts.length - 1]
    const downloadFilename = `${baseFilename}.${media.format}`

    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = downloadFilename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    await showToast('Download started', 'success')
  } catch (error) {
    console.error('❌ Error downloading media:', error)
    await showToast('Error downloading media', 'danger')
  }
}

async function deleteMedia(media) {
  trackEvent('Deleted Media', { mediaType: media.resourceType })
  const alert = await alertController.create({
    header: 'Delete Media',
    message: 'Are you sure you want to delete this media? This action cannot be undone.',
    buttons: [
      {
        text: 'Cancel',
        role: 'cancel',
      },
      {
        text: 'Delete',
        role: 'destructive',
        handler: async () => {
          const loading = await loadingController.create({
            message: 'Deleting media...',
          })
          await loading.present()

          try {
            await deleteDoc(doc(mediaCollection, media.id))
            await showToast('Media deleted successfully', 'success')
          } catch (error) {
            console.error('❌ Error deleting media:', error)
            await showToast('Error deleting media', 'danger')
          }

          await loading.dismiss()
        },
      },
    ],
  })

  await alert.present()
}

// Utility functions

async function showToast(message, color = 'primary') {
  const toast = await toastController.create({
    message,
    duration: 3000,
    color,
    position: 'bottom',
  })
  await toast.present()
}
</script>

<style scoped>
.media-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;
}
</style>
